import { useMemo } from 'react'

import { Features } from 'generated/featureDefinitions'
import { JobCatalogTreeVisibilityDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

interface JobCatalogTreeVisibility {
  canSeeTree: boolean
  canSeeCompetencies: boolean
  canSeeItManagement: boolean
  canSeeImport: boolean
}

export const useJobCatalogTreeVisibility = (): JobCatalogTreeVisibility => {
  const { company } = useCurrentSession()
  const { data } = useQuery(JobCatalogTreeVisibilityDocument, {
    variables: {
      companyId: company.id,
      featureAvailabilityIds: [Features.competencies_core, Features.it_management_v2_core],
    },
    fetchPolicy: 'cache-first',
  })

  const featureAvailabilities = data?.companies.featureAvailabilitiesConnection.nodes

  const canSeeTree = data?.jobCatalog._policies.jobCatalogTreeSee.can ?? false
  const canManageTree = data?.jobCatalog._policies.jobCatalogTreeManage.can ?? false
  const canImportResources = data?.universalImporter._policies.importResources.can ?? false

  const isCompetenciesFeatureEnabled = useMemo(
    () => isFeatureAvailabilityEnabled(featureAvailabilities ?? [], Features.competencies_core),
    [featureAvailabilities]
  )
  const isItManagementFeatureEnabled = useMemo(
    () => isFeatureAvailabilityEnabled(featureAvailabilities ?? [], Features.it_management_v2_core),
    [featureAvailabilities]
  )

  return {
    canSeeTree,
    canSeeCompetencies: isCompetenciesFeatureEnabled,
    canSeeItManagement: isItManagementFeatureEnabled,
    canSeeImport: canImportResources && canManageTree,
  }
}
